<template>
  <v-dialog v-model="dialog.show" max-width="780" persistent>
    <v-card>
      <v-card-title>
        {{
          dialog.mode === "create"
            ? $t("settings.profile.emailAccounts.dialog.create.title")
            : $t("settings.profile.emailAccounts.dialog.edit.title")
        }}
      </v-card-title>
      <v-divider />
      <v-card-text class="pt-4">
        <v-row dense>
          <v-col cols="12" md="6">
            <v-text-field
              v-model="dialog.data.name"
              :label="$t('settings.profile.emailAccounts.dialog.fields.name')"
              :rules="[required]"
              required
            />
          </v-col>
          <v-col cols="12" md="6">
            <v-select
              v-model="dialog.data.transportMode"
              :items="transportOptions"
              :label="$t('settings.profile.emailAccounts.dialog.fields.transportMode')"
            />
          </v-col>
          <v-col cols="12">
            <v-textarea
              v-model="dialog.data.description"
              :label="$t('settings.profile.emailAccounts.dialog.fields.description')"
              rows="2"
              auto-grow
            />
          </v-col>
          <v-col v-if="dialog.mode === 'edit'" cols="12" md="6">
            <v-select
              v-model="dialog.data.status"
              :items="statusOptions"
              :label="$t('settings.profile.emailAccounts.dialog.fields.status')"
            />
          </v-col>
          <v-col cols="12" md="6" class="d-flex align-center">
            <v-checkbox
              v-model="dialog.data.isDefault"
              :label="$t('settings.profile.emailAccounts.dialog.fields.isDefault')"
              density="comfortable"
              hide-details
            />
          </v-col>
        </v-row>

        <v-alert
          v-if="dialog.data.transportMode === 'company_default'"
          type="info"
          density="compact"
          class="mb-3"
        >
          {{ $t("settings.profile.emailAccounts.dialog.companyDefaultHint") }}
        </v-alert>

        <template v-if="dialog.data.transportMode === 'own_smtp'">
          <v-divider class="my-3" />

          <v-row dense>
            <v-col cols="12" md="6">
              <v-select
                v-model="dialog.data.providerKey"
                :items="providerOptions"
                :label="$t('settings.profile.emailAccounts.dialog.fields.provider')"
                @update:model-value="handleProviderChange"
              />
            </v-col>
            <v-col cols="12" md="6">
              <v-select
                v-model="dialog.data.authType"
                :items="authOptions"
                :label="$t('settings.profile.emailAccounts.dialog.fields.authType')"
              />
            </v-col>
            <v-col cols="12" md="7">
              <v-text-field
                v-model="dialog.data.host"
                :label="$t('settings.profile.emailAccounts.dialog.fields.host')"
                autocomplete="off"
              />
            </v-col>
            <v-col cols="12" md="5">
              <v-text-field
                v-model.number="dialog.data.port"
                :label="$t('settings.profile.emailAccounts.dialog.fields.port')"
                type="number"
                min="1"
                max="65535"
              />
            </v-col>
            <v-col cols="12" md="6">
              <v-checkbox
                v-model="dialog.data.secure"
                :label="$t('settings.profile.emailAccounts.dialog.fields.secure')"
                density="comfortable"
                hide-details
              />
            </v-col>
            <v-col cols="12" md="6">
              <v-checkbox
                v-model="dialog.data.requireTLS"
                :label="$t('settings.profile.emailAccounts.dialog.fields.requireTLS')"
                density="comfortable"
                hide-details
              />
            </v-col>
            <v-col v-if="dialog.data.authType === 'password'" cols="12" md="6">
              <v-text-field
                v-model="dialog.data.username"
                :label="$t('settings.profile.emailAccounts.dialog.fields.username')"
                autocomplete="off"
              />
            </v-col>
            <v-col
              v-if="dialog.mode === 'edit' && dialog.data.authType === 'password'"
              cols="12"
              md="6"
              class="d-flex align-center"
            >
              <v-checkbox
                v-model="dialog.editPassword"
                :label="$t('settings.profile.emailAccounts.dialog.editPassword')"
                density="comfortable"
                hide-details
              />
            </v-col>
            <v-col v-if="showPasswordField" cols="12">
              <v-text-field
                v-model="dialog.data.password"
                :label="$t('settings.profile.emailAccounts.dialog.fields.password')"
                type="password"
                autocomplete="new-password"
              />
            </v-col>
          </v-row>
        </template>

        <v-divider class="my-3" />

        <v-row dense>
          <v-col cols="12" md="6">
            <v-text-field
              v-model="dialog.data.fromEmail"
              :label="$t('settings.profile.emailAccounts.dialog.fields.fromEmail')"
              :rules="[required]"
              autocomplete="off"
              required
            />
          </v-col>
          <v-col cols="12" md="6">
            <v-text-field
              v-model="dialog.data.fromName"
              :label="$t('settings.profile.emailAccounts.dialog.fields.fromName')"
            />
          </v-col>
          <v-col cols="12">
            <v-text-field
              v-model="dialog.data.replyToEmail"
              :label="$t('settings.profile.emailAccounts.dialog.fields.replyToEmail')"
            />
          </v-col>
        </v-row>

        <v-alert v-if="dialog.error" type="error" class="mt-2">
          {{ dialog.error }}
        </v-alert>
      </v-card-text>
      <v-divider />
      <v-card-actions>
        <v-spacer />
        <v-btn variant="text" :disabled="dialog.saving" @click="$emit('close')">
          {{ $t("settings.profile.emailAccounts.dialog.actions.cancel") }}
        </v-btn>
        <v-btn color="primary" :loading="dialog.saving" @click="$emit('save')">
          {{ $t("settings.profile.emailAccounts.dialog.actions.save") }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
export default {
  name: "UserEmailAccountFormDialog",
  props: {
    dialog: {
      type: Object,
      required: true,
    },
    providerOptions: {
      type: Array,
      default: () => [],
    },
    transportOptions: {
      type: Array,
      default: () => [],
    },
    statusOptions: {
      type: Array,
      default: () => [],
    },
    authOptions: {
      type: Array,
      default: () => [],
    },
  },
  emits: ["close", "save", "provider-change"],
  computed: {
    showPasswordField() {
      if (this.dialog.data.transportMode !== "own_smtp") return false;
      if (this.dialog.data.authType !== "password") return false;
      return this.dialog.mode === "create" || this.dialog.editPassword;
    },
  },
  methods: {
    required(value) {
      return (
        !!String(value || "").trim() ||
        this.$t("settings.profile.emailAccounts.dialog.requiredField")
      );
    },
    handleProviderChange(providerKey) {
      this.$emit("provider-change", providerKey);
    },
  },
};
</script>
